import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, Check, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { INDIAN_STATES, INDIAN_CITIES, getCitiesByCountry } from '@/data/locationData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileCompletionModal = ({ isOpen, onClose, customer, token, onProfileUpdated, required = false }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: customer?.company_name || '',
    state: customer?.state || '',
    city: customer?.city || '',
    pincode: customer?.pincode || '',
    address_line1: customer?.address_line1 || '',
    address_line2: customer?.address_line2 || '',
    international_address: customer?.international_address || '',
    private_marka: customer?.private_marka || '',
    gst_number: customer?.gst_number || '',
    is_urp: customer?.gst_number === 'URP' || customer?.is_urp || false,
    preferred_transporter: customer?.preferred_transporter || '',
    preferred_courier: customer?.preferred_courier || ''
  });
  
  const [stateOpen, setStateOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  
  const isIndia = (customer?.country_code || '91') === '91';
  const isDelhi = form.state?.toLowerCase() === 'delhi' || customer?.state?.toLowerCase() === 'delhi';
  
  // Check which fields are missing (to show only required fields)
  const needsCompanyName = !customer?.company_name;
  const needsState = isIndia && !customer?.state;
  const needsCity = !customer?.city;
  const needsPincode = isIndia && !customer?.pincode;
  const needsAddress = isIndia && !customer?.address_line1;
  const needsGstDetails = isIndia && !customer?.gst_number && !customer?.is_urp;
  const needsPrivateMarka = !customer?.private_marka;
  const needsTransportDetails = isIndia && !isDelhi && !customer?.preferred_transporter;
  const needsCourierDetails = isIndia && !isDelhi && !customer?.preferred_courier;
  
  // Count how many fields are missing
  const missingFieldsCount = [
    needsCompanyName, needsState, needsCity, needsPincode, needsAddress,
    needsGstDetails, needsPrivateMarka, needsTransportDetails, needsCourierDetails
  ].filter(Boolean).length;
  
  // Update form when customer changes
  useEffect(() => {
    if (customer) {
      setForm({
        company_name: customer.company_name || '',
        state: customer.state || '',
        city: customer.city || '',
        pincode: customer.pincode || '',
        address_line1: customer.address_line1 || '',
        address_line2: customer.address_line2 || '',
        international_address: customer.international_address || '',
        private_marka: customer.private_marka || '',
        gst_number: customer.gst_number === 'URP' ? '' : (customer.gst_number || ''),
        is_urp: customer.gst_number === 'URP' || customer.is_urp || false,
        preferred_transporter: customer.preferred_transporter || '',
        preferred_courier: customer.preferred_courier || ''
      });
    }
  }, [customer]);
  
  // Get available cities based on country/state
  const availableCities = useMemo(() => {
    if (isIndia && form.state) {
      return INDIAN_CITIES[form.state] || [];
    } else if (!isIndia) {
      return getCitiesByCountry(customer?.country_code || '91');
    }
    return [];
  }, [isIndia, form.state, customer?.country_code]);
  
  const handleChange = (field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Reset dependent fields when state changes
      if (field === 'state') {
        updated.city = '';
        updated.pincode = '';
      }
      
      // Reset pincode when city changes
      if (field === 'city') {
        updated.pincode = '';
      }
      
      return updated;
    });
  };
  
  const validateForm = () => {
    if (!form.company_name.trim()) {
      toast.error('Company name is mandatory');
      return false;
    }
    
    if (isIndia) {
      if (!form.state) {
        toast.error('Please select your state');
        return false;
      }
      if (!form.city) {
        toast.error('Please select your city');
        return false;
      }
      if (!form.pincode || form.pincode.length !== 6) {
        toast.error('Please enter a valid 6-digit pincode');
        return false;
      }
      if (!form.address_line1.trim()) {
        toast.error('Address Line 1 is mandatory');
        return false;
      }
    } else {
      if (!form.city) {
        toast.error('Please select your city');
        return false;
      }
    }
    
    // Private Marka validation (mandatory for all)
    if (!form.private_marka.trim()) {
      toast.error('Private Marka is mandatory');
      return false;
    }
    
    // GST validation (mandatory for Indian customers)
    if (isIndia) {
      if (!form.is_urp && !form.gst_number.trim()) {
        toast.error('Please enter GST Number or select URP (Unregistered Person)');
        return false;
      }
      if (!form.is_urp && form.gst_number.trim().length !== 15) {
        toast.error('GST Number must be 15 characters');
        return false;
      }
    }
    
    // Transport/Courier validation (mandatory for non-Delhi Indian customers)
    if (isIndia && !isDelhi) {
      if (!form.preferred_transporter.trim()) {
        toast.error('Preferred Transporter is mandatory for non-Delhi customers');
        return false;
      }
      if (!form.preferred_courier.trim()) {
        toast.error('Preferred Courier is mandatory for non-Delhi customers');
        return false;
      }
    }
    
    return true;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      // Format address for storage
      let formattedAddress = '';
      if (isIndia) {
        formattedAddress = [
          form.address_line1,
          form.address_line2,
          form.city,
          form.state,
          form.pincode
        ].filter(Boolean).join(', ');
      } else {
        formattedAddress = [
          form.international_address,
          form.city
        ].filter(Boolean).join(', ');
      }
      
      // Determine GST value
      const gstValue = form.is_urp || !isIndia ? 'URP' : form.gst_number.trim().toUpperCase();
      
      const updateData = {
        ...form,
        address: formattedAddress,
        gst_number: gstValue,
        is_urp: form.is_urp || !isIndia,
        profile_complete: true,
        profile_incomplete: false  // Clear the quick-register flag
      };
      
      const response = await axios.put(`${API}/auth/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Profile updated successfully!');
        onProfileUpdated(response.data.customer);
        onClose();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md bg-white max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-bold text-gray-900">
            <AlertCircle className="w-5 h-5 text-red-500" />
            Complete Required Details
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Please fill in the following {missingFieldsCount} missing field{missingFieldsCount > 1 ? 's' : ''} to continue.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Company Name - only if missing */}
          {needsCompanyName && (
            <div>
              <Label className="text-gray-700 font-medium">Company Name *</Label>
              <Input
                value={form.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className="mt-1 bg-white border-gray-300"
                placeholder="Your company name"
                autoFocus
              />
            </div>
          )}
          
          {/* State - only if missing (India only) */}
          {needsState && (
            <div>
              <Label className="text-gray-700 font-medium">State *</Label>
              <Popover open={stateOpen} onOpenChange={setStateOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full mt-1 justify-between bg-white border-gray-300">
                    {form.state || "Select state..."}
                    <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 bg-white" align="start">
                  <Command>
                    <CommandInput placeholder="Search state..." />
                    <CommandList>
                      <CommandEmpty>No state found.</CommandEmpty>
                      <CommandGroup className="max-h-[200px] overflow-y-auto">
                        {INDIAN_STATES.map((state) => (
                          <CommandItem key={state} value={state} onSelect={() => { handleChange('state', state); setStateOpen(false); }}>
                            {state}
                            <Check className={cn("ml-auto h-4 w-4", form.state === state ? "opacity-100" : "opacity-0")} />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
          
          {/* City - only if missing */}
          {needsCity && (
            <div>
              <Label className="text-gray-700 font-medium">City *</Label>
              <Popover open={cityOpen} onOpenChange={setCityOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full mt-1 justify-between bg-white border-gray-300" disabled={isIndia && !form.state}>
                    {form.city || (isIndia && !form.state ? "Select state first" : "Select city...")}
                    <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 bg-white" align="start">
                  <Command>
                    <CommandInput placeholder="Search city..." />
                    <CommandList>
                      <CommandEmpty>No city found.</CommandEmpty>
                      <CommandGroup className="max-h-[200px] overflow-y-auto">
                        {availableCities.map((city) => (
                          <CommandItem key={city} value={city} onSelect={() => { handleChange('city', city); setCityOpen(false); }}>
                            {city}
                            <Check className={cn("ml-auto h-4 w-4", form.city === city ? "opacity-100" : "opacity-0")} />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
          
          {/* Pincode - only if missing (India only) */}
          {needsPincode && (
            <div>
              <Label className="text-gray-700 font-medium">Pincode *</Label>
              <Input
                type="text"
                value={form.pincode}
                onChange={(e) => handleChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="mt-1 bg-white border-gray-300"
                placeholder="Enter 6-digit pincode"
                maxLength={6}
              />
            </div>
          )}
          
          {/* Address - only if missing (India only) */}
          {needsAddress && (
            <div>
              <Label className="text-gray-700 font-medium">Address *</Label>
              <Input
                value={form.address_line1}
                onChange={(e) => handleChange('address_line1', e.target.value)}
                className="mt-1 bg-white border-gray-300"
                placeholder="Building, Street, Area"
              />
            </div>
          )}
          
          {/* Private Marka - only if missing */}
          {needsPrivateMarka && (
            <div>
              <Label className="text-gray-700 font-medium">Private Marka *</Label>
              <Input
                value={form.private_marka}
                onChange={(e) => handleChange('private_marka', e.target.value)}
                className="mt-1 bg-white border-gray-300"
                placeholder="Your identification mark for invoices"
              />
              <p className="text-xs text-gray-500 mt-1">This appears on all your invoices and slips</p>
            </div>
          )}
          
          {/* GST Number - only if missing (India only) */}
          {needsGstDetails && (
            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">GST Number *</Label>
              <Input
                value={form.gst_number}
                onChange={(e) => handleChange('gst_number', e.target.value.toUpperCase())}
                disabled={form.is_urp}
                maxLength={15}
                className={`mt-1 bg-white border-gray-300 uppercase ${form.is_urp ? 'opacity-50' : ''}`}
                placeholder="15-character GST Number"
              />
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="profile-urp"
                  checked={form.is_urp}
                  onChange={(e) => { handleChange('is_urp', e.target.checked); if (e.target.checked) handleChange('gst_number', ''); }}
                  className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <Label htmlFor="profile-urp" className="text-sm text-gray-700 cursor-pointer">
                  I don't have GST (Unregistered Person)
                </Label>
              </div>
            </div>
          )}
          
          {/* Preferred Transporter - only if missing (non-Delhi India only) */}
          {needsTransportDetails && (
            <div>
              <Label className="text-gray-700 font-medium">Preferred Transporter *</Label>
              <Input
                value={form.preferred_transporter}
                onChange={(e) => handleChange('preferred_transporter', e.target.value)}
                className="mt-1 bg-white border-gray-300"
                placeholder="e.g., VRL, DTDC, Professional"
              />
            </div>
          )}
          
          {/* Preferred Courier - only if missing (non-Delhi India only) */}
          {needsCourierDetails && (
            <div>
              <Label className="text-gray-700 font-medium">Preferred Courier *</Label>
              <Input
                value={form.preferred_courier}
                onChange={(e) => handleChange('preferred_courier', e.target.value)}
                className="mt-1 bg-white border-gray-300"
                placeholder="e.g., Delhivery, BlueDart, FedEx"
              />
            </div>
          )}
          
          {/* Save Button */}
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="w-full bg-red-600 hover:bg-red-700 text-white mt-6"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save & Proceed'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileCompletionModal;
